"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MGMAuth = void 0;
const index_1 = require("./index");
const okta_auth_js_1 = require("@okta/okta-auth-js");
const crosstab_1 = require("./services/crosstab");
const config_1 = require("./config");
const http_1 = require("./services/http");
const errors_1 = require("./@types/errors");
const mgm_client_logging_1 = require("@mgmresorts/mgm-client-logging");
const utils_1 = require("./utils");
class MGMAuth {
    constructor(config) {
        this.refreshTimeoutId = null;
        this.currentTokenExpiresAt = 0;
        this._logger = undefined;
        this.enrichToken = (token) => {
            if (token != null) {
                const { payload: tokenPayload } = this.authClient.token.decode(token.accessToken);
                const isTransient = Array.isArray(tokenPayload.groups) && tokenPayload.groups.includes(index_1.TRANSIENT_GROUP);
                const loyaltyTier = 'com.mgm.loyalty.tier' in tokenPayload ? tokenPayload['com.mgm.loyalty.tier'] : null;
                const isPerpetual = 'com.mgm.loyalty.perpetual_eligible' in tokenPayload
                    ? Boolean(tokenPayload['com.mgm.loyalty.perpetual_eligible'])
                    : false;
                const { mlife } = tokenPayload || { mlife: undefined };
                return Object.assign(Object.assign({}, token), { isTransient, isPerpetual, loyaltyTier, mlife, value: token.accessToken });
            }
            return undefined;
        };
        const identityConfig = config.environment ? Object.assign(Object.assign({}, config_1.defaultConfig[config.environment]), config) : Object.assign({}, config);
        const { identityAuthOrigin = 'https://www.mgmresorts.com/identity' } = identityConfig, 
        // storageIdentifier = STORAGE_IDENTIFIER,
        oktaConfig = __rest(identityConfig, ["identityAuthOrigin"]);
        // TODO: allow this to be set by config again
        const storageIdentifier = index_1.STORAGE_IDENTIFIER; // override this to the common one
        this.config = Object.assign(Object.assign({}, identityConfig), { identityAuthOrigin,
            storageIdentifier });
        this.oktaConfig = oktaConfig;
        const tokenManagerConfig = oktaConfig.tokenManager ? Object.assign({}, oktaConfig.tokenManager) : {};
        this.tokenStorageKey = `${storageIdentifier}-storage`;
        tokenManagerConfig.storageKey = this.tokenStorageKey;
        if (typeof window !== 'undefined') {
            tokenManagerConfig.storage = 'localStorage';
        }
        else {
            tokenManagerConfig.storage = 'memory';
        }
        this.authClient = new okta_auth_js_1.OktaAuth(Object.assign(Object.assign({ scopes: this.getAuthenticatedScopes() }, oktaConfig), { tokenManager: Object.assign({}, tokenManagerConfig) }));
        this.crossTab = new crosstab_1.CrossTab();
        this.initializeCrossTab();
        this.initializeOktaHooks();
    }
    get logger() {
        var _a, _b, _c, _d;
        if (typeof window !== 'undefined' && ((_a = window.mgm) === null || _a === void 0 ? void 0 : _a.logger)) {
            return window.mgm.logger;
        }
        if (!this._logger) {
            this._logger = new mgm_client_logging_1.Logger({
                appName: (_b = this.config.appName) !== null && _b !== void 0 ? _b : 'MGMAuth',
                logLevel: (_c = this.config.logLevel) !== null && _c !== void 0 ? _c : mgm_client_logging_1.LogLevel.warn,
                debugMode: (_d = this.config.debugMode) !== null && _d !== void 0 ? _d : false,
            });
        }
        return this._logger;
    }
    /**
     * Set up the cross tab listeners so we can alert the application to sign in/out that happens on other tabs
     */
    initializeCrossTab() {
        this.crossTab.on('sign-in', () => {
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onSignIn && (yield this.onSignIn());
            });
            call();
        });
        this.crossTab.on('sign-out', (data) => {
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onSignOut && (yield this.onSignOut(data));
            });
            call();
        });
    }
    initializeTokenRefresh(expiresAt) {
        var _a;
        this.logger.trace('Start initializeTokenRefresh');
        if (this.currentTokenExpiresAt !== expiresAt) {
            this.currentTokenExpiresAt = expiresAt;
            const minutesToRefreshPriorToExpiration = (_a = this.config.minutesToRefreshPriorToExpiration) !== null && _a !== void 0 ? _a : 1;
            const refreshTime = expiresAt * 1000 - minutesToRefreshPriorToExpiration * 60000;
            const refreshTimeout = refreshTime - Number(new Date());
            if (this.refreshTimeoutId) {
                clearTimeout(this.refreshTimeoutId);
            }
            this.refreshTimeoutId = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                yield this.refreshCurrentToken();
            }), Math.max(refreshTimeout, 60000));
            this.logger.trace('End initializeTokenRefresh (Set refresh timeout)');
        }
        else {
            this.logger.trace('End initializeTokenRefresh (Expiration time matches existing timeout)');
        }
    }
    /**
     * Set up the Okta listeners so that we can take action if needed and also call the application callback if provided.
     */
    initializeOktaHooks() {
        const handleRemoved = (key, token) => {
            this.logger.trace('Token Removed', { key });
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onTokenRemoved && (yield this.onTokenRemoved(key, token));
            });
            call();
        };
        const handleAdded = (key, token) => {
            this.logger.trace('Token Added', { key });
            if (key === index_1.tokenKeys.accessToken) {
                this.logger.trace('Token Added: Initializing Refresh', { key });
                this.initializeTokenRefresh(token.expiresAt);
            }
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onTokenAdded && (yield this.onTokenAdded(key, token));
            });
            call();
        };
        const handleExpired = (key, expiredToken) => {
            this.logger.trace('Token Expired', { key });
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onTokenExpired && (yield this.onTokenExpired(key, expiredToken));
            });
            call();
        };
        const handleRenewed = (key, newToken, oldToken) => {
            this.logger.trace('Token Renewed', { key });
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onTokenRenewed && (yield this.onTokenRenewed(key, newToken, oldToken));
            });
            call();
        };
        const handleError = error => {
            this.logger.trace('Token Error', { code: error.errorCode, summary: error.errorSummary, message: error.message });
            const call = () => __awaiter(this, void 0, void 0, function* () {
                this.onTokenError && (yield this.onTokenError(error));
            });
            call();
        };
        this.authClient.tokenManager.on(okta_auth_js_1.EVENT_REMOVED, handleRemoved);
        this.authClient.tokenManager.on(okta_auth_js_1.EVENT_ADDED, handleAdded);
        this.authClient.tokenManager.on(okta_auth_js_1.EVENT_EXPIRED, handleExpired);
        this.authClient.tokenManager.on(okta_auth_js_1.EVENT_RENEWED, handleRenewed);
        this.authClient.tokenManager.on(okta_auth_js_1.EVENT_ERROR, handleError);
    }
    /**
     * Set up an iframe that is going to make a call to an Okta endpoint.  This function:
     * - Creates an iframe
     * - Sets up a timeout so the action will "fail" instead of just never resolving
     * - Sets up a window message listener so we know the final page loaded
     * - Calls back for success or timeout
     * - Cleans up the iframe, timeout, and window message listener
     *
     * @param url - The Okta URL we want to load
     * @param eventName - The event name that we will listen for to indicate the process is done.
     * @param timeoutCallback - A callback that is triggered if the action does not complete in time
     * @param successCallback - A callback that is triggered when we receive the eventName message
     * @param timeoutInSeconds - The number of seconds we should wait for the action to complete (default 15, the same as Okta internal timeouts)
     */
    loadOktaIframe(url, eventName, timeoutCallback, successCallback, timeoutInSeconds = 15) {
        this.logger.trace('Start loadOktaIframe', { url, eventName });
        const iframe = document.createElement('iframe');
        iframe.setAttribute('id', 'MGM_IDENTITY_IFRAME');
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.setAttribute('loading', 'eager');
        iframe.setAttribute('src', url);
        iframe.setAttribute('hidden', 'true');
        this.logger.log(`[MGMAuth] Sending Okta request: ${url}`);
        const timeout = setTimeout(() => {
            // eslint-disable-next-line no-use-before-define
            window.removeEventListener('message', messageHandler);
            document.body.removeChild(iframe);
            this.logger.trace('End loadOktaIframe (Timeout)');
            timeoutCallback(new Error('Operation Timeout'));
        }, timeoutInSeconds * 1000);
        // eslint-disable-next-line no-undef
        const messageHandler = (event) => {
            this.logger.log(`[MGMAuth] Message received: ${event.data.type} / ${event.data.event} / ${event.origin}`);
            if (event.data.type === '[MGM Auth]') {
                switch (event.data.event) {
                    case eventName:
                        clearTimeout(timeout);
                        window.removeEventListener('message', messageHandler);
                        document.body.removeChild(iframe);
                        this.logger.trace('End loadOktaIframe (Success)');
                        successCallback(event.data.url ? new URL(event.data.url) : undefined);
                        break;
                }
            }
            else {
                this.logger.warn('[MGMAuth] Message ignored');
            }
        };
        window.addEventListener('message', messageHandler);
        document.body.appendChild(iframe);
    }
    /**
     * Refreshes the current token and returns an enriched access token
     *
     * * A token must be present in order for this function to be executed successfully.
     *
     * @returns An enriched access token, or undefined if the token is not present
     */
    refreshCurrentToken() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.logger.trace('Start refreshCurrentToken');
            const session = yield this.authClient.session.get();
            if (session.status === "ACTIVE" /* SessionStatus.active */) {
                yield this.performSilentLogin(this.sessionHasTransientUser(session));
            }
            else if ((_a = (yield this.getAccessToken())) === null || _a === void 0 ? void 0 : _a.isTransient) {
                const tokenAcquired = yield this.acquireTransientToken();
                if (!tokenAcquired) {
                    this.logger.debug('refreshCurrentToken unable to acquire common anon token');
                }
            }
            else {
                this.logger.trace('End refreshCurrentToken (No Active Session)');
                return undefined;
            }
            this.logger.trace('End refreshCurrentToken (Success)');
            return yield this.getAccessToken();
        });
    }
    /**
     * Refetches the current token if response header X-MGM-Identity value is action:re-fetch-token and updates the current operation context
     *
     * @returns Updated headers with refreshed token
     */
    handleIdentityResponse(error, headers) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.logger.trace('Start handleIdentityResponse');
            if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.headers.get('X-MGM-Identity')) === 'action:re-fetch-token') {
                const token = yield this.refreshCurrentToken();
                this.logger.trace('End handleIdentityResponse (X-MGM-Identity:action:re-fetch-token response header found and updated config is returned)');
                return Object.assign(Object.assign({}, headers), { authorization: token && (token === null || token === void 0 ? void 0 : token.accessToken) ? `Bearer ${token === null || token === void 0 ? void 0 : token.accessToken}` : undefined });
            }
            this.logger.trace('End handleIdentityResponse (X-MGM-Identity:action:re-fetch-token response header not found)');
            return undefined;
        });
    }
    /**
     * Acquires tokens for a user. This process can acquire a transient token, or required that the user is logged in
     *
     * - Check to see if we already have a valid session with Okta (User is logged in, or we already have a transient session and allow transient user)
     * - Return access token if valid session exists
     * - Return undefined if no session exists or we do not allow transient users
     * - Initiate a call to get a transient token
     * - Return the token either via the session or a code in the URL
     *
     * @param allowTransient - allow this process to acquire a transient token
     * @returns An access token if we are able to acquire one, or undefined if we are not able to
     *
     * Possible Exceptions
     * - Operation Timeout: the call to authorize did not complete within the alloted time frame (10 seconds)
     * - Authorize URL is not defined: The authorizeUrl is required (it is defaulted if you use the environment config)
     * - Client ID is not defined: The clientId is required (it is defaulted if you use the environment config)
     * - Token acquisition failed: [Message]: There was an error from Okta when we attempted to get the token
     * - Unable to parse tokens from URL: [URL]: There was no code in the return from Okta, and the user does not have a valid session
     * - Unable to parse tokens from URL: not present: The URL was not returned from the identity token page
     */
    acquireUserTokens() {
        return __awaiter(this, arguments, void 0, function* (allowTransient = false) {
            this.logger.trace('Start acquireUserTokens');
            if (typeof window === 'undefined') {
                this.logger.warn('[MGMAuth]: This function (acquireUserTokens) cannot be used to retrieve a token server side');
                this.logger.trace('End acquireUserTokens (window is undefined)');
                return Promise.resolve(undefined);
            }
            // get the token or try and upgrade the token
            const isValidSession = yield this.isSessionValid(allowTransient);
            // if we already have a session and a token, get it and send it back to the app.
            if (isValidSession) {
                const token = yield this.getAccessToken();
                this.logger.trace('End acquireUserTokens (Success - valid token and session)');
                return Promise.resolve(token);
            }
            // if we don't have a session and cannot allow the transient user, then return
            if (!allowTransient) {
                this.logger.trace('End acquireUserTokens (Failure - Transient tokens not allowed)');
                return Promise.resolve(undefined);
            }
            const response = this.acquireTransientToken();
            this.logger.trace('End acquireUserTokens');
            return response;
        });
    }
    acquireTransientToken() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Starting acquireTransientToken');
            const tokenAcquired = yield this.getCommonTransientToken();
            if (tokenAcquired) {
                this.logger.trace('End acquireTransientToken');
                return Promise.resolve(this.getAccessToken());
            }
            else {
                this.logger.debug('acquireTransientToken unable to acquire common anon token');
            }
            // if any required config is missing, error out.
            if (!this.config.authorizeUrl) {
                this.logger.trace('End acquireTransientToken (Missing authorizeUrl)');
                return Promise.reject(new Error('Authorize URL is not defined'));
            }
            if (!this.config.clientId) {
                this.logger.trace('End acquireTransientToken (Missing clientId)');
                return Promise.reject(new Error('Client ID is not defined'));
            }
            if (!this.config.tokensAcceptedRedirectUri) {
                this.logger.trace('End acquireTransientToken (Missing tokensAcceptedRedirectUri)');
                return Promise.reject(new Error('Token Redirect URI is not defined'));
            }
        });
    }
    /**
     * @deprecated since version 3.0. Please transition to {@link acquireUserTokens}
     *
     * Acquires the users tokens with a redirect.  If a user is not logged in, it will attain the
     *  tokens for the transient (anonymous) user.
     */
    acquireTokens(options_1) {
        return __awaiter(this, arguments, void 0, function* (options, skipTransient = false) {
            var _a, _b;
            const isValidSession = yield this.isSessionValid();
            if (skipTransient && !isValidSession) {
                return;
            }
            if (!isValidSession) {
                // if the session is invalid, always get a new anonymous session
                // clients can upgrade later if they choose
                yield this.endSession();
                const scopes = new Set([...((_b = (_a = options === null || options === void 0 ? void 0 : options.scopes) !== null && _a !== void 0 ? _a : this.config.scopes) !== null && _b !== void 0 ? _b : []), ...index_1.transientScopes]);
                index_1.scopesOnlyForAuthedUsers.forEach(scope => scopes.delete(scope));
                options = options !== null && options !== void 0 ? options : {};
                // TODO: we need to discuss with identity about how we should get this value
                options.loginHint = index_1.TRANSIENT_CID;
                options.scopes = [...scopes];
            }
            yield this.authClient.token.getWithRedirect(options);
        });
    }
    /**
     * Get the current access token that is in storage
     *
     * @returns An enriched access token or undefined if it does not exist
     */
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const token = (yield this.authClient.tokenManager.get(index_1.tokenKeys.accessToken));
                if (token) {
                    this.initializeTokenRefresh(token.expiresAt);
                }
                return this.enrichToken(token);
            }
            catch (_a) {
                return undefined;
            }
        });
    }
    /**
     * Get the current ID token that is in storage. ID Token is verified if it exists in storage
     *
     * @returns An ID token or undefined if it does not exist
     */
    getIdToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const token = yield this.authClient.tokenManager.get('idToken');
                if (token != null) {
                    return yield this.authClient.token.verify(token);
                }
                return undefined;
            }
            catch (_a) {
                return undefined;
            }
        });
    }
    getRedirectQueryString(options, additionalDeepLinkedData) {
        var _a;
        const { clientId, redirectUri, scopes } = this.oktaConfig;
        // Inject the sign in required scopes
        const nextOptions = Object.assign(Object.assign({}, options), { scopes: [...(scopes !== null && scopes !== void 0 ? scopes : []), ...((_a = options === null || options === void 0 ? void 0 : options.scopes) !== null && _a !== void 0 ? _a : [])] });
        let nextRedirectUri = (options === null || options === void 0 ? void 0 : options.redirectUri) ? options === null || options === void 0 ? void 0 : options.redirectUri : redirectUri;
        if (nextRedirectUri && nextRedirectUri.match(/\?/)) {
            nextRedirectUri = encodeURIComponent(nextRedirectUri);
        }
        return `?client_id=${(options === null || options === void 0 ? void 0 : options.clientId) || clientId}&redirect_uri=${nextRedirectUri}&scopes=${nextOptions.scopes}${additionalDeepLinkedData || ''}`;
    }
    get signInUrl() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.config.identityAuthOrigin;
    }
    get signUpUrl() {
        return `${this.config.identityAuthOrigin}/signup`;
    }
    get activateUrl() {
        return `${this.config.identityAuthOrigin}/activate`;
    }
    get changePasswordUrl() {
        return `${this.config.identityAuthOrigin}/change-password`;
    }
    get changeEmailUrl() {
        return `${this.config.identityAuthOrigin}/change-email`;
    }
    /**
     * Redirects the user to the identity sign in page.
     *
     * Ends the current session if one exists before it sends the user to the identity widget
     *
     * @param options Options for the redirect.  This will default from the auth config if not provided
     * @param additionalDeepLinkedData Additional data to include in the redirect (must start with `&`)
     * @param replaceHistory Boolean flag to determine whether the current page will or will not be saved in session History. If `true`, the user won't be able to use the browser back button to navigate to the current page.
     */
    goToSignIn(options_1, additionalDeepLinkedData_1) {
        return __awaiter(this, arguments, void 0, function* (options, additionalDeepLinkedData, replaceHistory = false) {
            yield this.endSession();
            if (replaceHistory) {
                window.location.replace(`${this.signInUrl}${this.getRedirectQueryString(options, additionalDeepLinkedData)}`);
            }
            else {
                window.location.href = `${this.signInUrl}${this.getRedirectQueryString(options, additionalDeepLinkedData)}`;
            }
        });
    }
    isValidPreEnrollmentState() {
        return __awaiter(this, void 0, void 0, function* () {
            const { activeMemberSession } = yield this.hasActiveMemberSession();
            if (activeMemberSession) {
                throw Error('There is an existing member session, you need to logout before you take this action');
            }
            return true;
        });
    }
    /**
     * Redirects the user to the identity sign up page.
     *
     * Ensures that the user is not logged in before doing the redirect (Identity widget will bounce the user back if they are already signed in)
     *
     * @param options Options for the redirect.  This will default from the auth config if not provided
     * @param additionalDeepLinkedData Additional data to include in the redirect (must start with `&`)
     *
     * Possible Exceptions
     * - There is an existing member session, you need to logout before you take this action: You cannot perform this action if you're signed in.
     */
    goToSignUp(options, additionalDeepLinkedData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(yield this.isValidPreEnrollmentState())) {
                return;
            }
            window.location.href = `${this.signUpUrl}${this.getRedirectQueryString(options, additionalDeepLinkedData)}`;
        });
    }
    /**
     * Redirects the user to the activate page.
     *
     * Ensures that the user is not logged in before doing the redirect (Identity widget will bounce the user back if they are already signed in)
     *
     * @param options Options for the redirect.  This will default from the auth config if not provided
     * @param additionalDeepLinkedData Additional data to include in the redirect (must start with `&`)
     *
     * Possible Exceptions
     * - There is an existing member session, you need to logout before you take this action: You cannot perform this action if you're signed in.
     */
    goToActivateAccount(options, additionalDeepLinkedData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(yield this.isValidPreEnrollmentState())) {
                return;
            }
            window.location.href = `${this.activateUrl}${this.getRedirectQueryString(options, additionalDeepLinkedData)}`;
        });
    }
    /**
     * Redirects the user to the change password page
     *
     * Ensures that the user is logged in before doing the redirect (Identity widget will bounce the user back if they are already signed in)
     *
     * @param options Options for the redirect.  This will default from the auth config if not provided
     * @param additionalDeepLinkedData Additional data to include in the redirect (must start with `&`)
     *
     * Possible Exceptions
     * - You must be logged in in order to change your password: You must be logged in in order to change your password.
     */
    goToChangePassword(options, additionalDeepLinkedData) {
        return __awaiter(this, void 0, void 0, function* () {
            const { activeMemberSession } = yield this.hasActiveMemberSession();
            if (activeMemberSession) {
                window.location.href = `${this.changePasswordUrl}${this.getRedirectQueryString(options, additionalDeepLinkedData)}`;
            }
            else {
                throw Error('You must be logged in in order to change your password');
            }
        });
    }
    /**
     * Signs the user out of your application and Okta.
     *
     * - A call to the supplementalSignOutUrl is made
     * - A call to {@link endSession} is made (to revoke the activation token and end the session)
     * - If an ID Token is present, then a full log out of the user from Okta is done
     *
     * @returns Promise<void>
     *
     * Possible Exceptions:
     * - Operation Timeout: the call to logout did not complete within the alloted time frame (10 seconds)
     * - Post Logout Redirect URI is not set: The postLogoutRedirectUri is required (it is defaulted if you use the environment config)
     */
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Start signOut');
            if (!this.authClient.tokenManager) {
                this.logger.trace('End signOut (No token manager)');
                return Promise.resolve();
            }
            if (!this.config.postLogoutRedirectUri) {
                this.logger.trace('End signOut (Missing postLogoutRedirectUri)');
                return Promise.reject(new Error('Post Logout Redirect URI is not set'));
            }
            // get the idToken so we can end the okta session
            const idToken = (yield this.authClient.tokenManager.getTokens()).idToken;
            // terminate the local app tokens and session
            yield this.endSession();
            return new Promise((resolve, reject) => {
                var _a;
                if (idToken) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const logoutRedirectUri = new URL(this.config.postLogoutRedirectUri);
                    const logoutUrl = new URL(`${this.config.issuer}/v1/logout`);
                    logoutUrl.searchParams.append('id_token_hint', (_a = idToken === null || idToken === void 0 ? void 0 : idToken.idToken) !== null && _a !== void 0 ? _a : '');
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    logoutUrl.searchParams.append('post_logout_redirect_uri', logoutRedirectUri.toString());
                    this.loadOktaIframe(logoutUrl.toString(), 'Sign out complete', (e) => {
                        this.logger.error(e);
                        this.logger.trace('End signOut (Error State)');
                        reject(e);
                    }, () => {
                        this.logger.trace('End signOut (Success)');
                        this.crossTab.emit({
                            key: 'sign-out',
                            data: {}, // { storageKey: this.config.storageIdentifier },
                        });
                        resolve();
                    });
                }
                else {
                    this.crossTab.emit({
                        key: 'sign-out',
                        data: {}, // { storageKey: this.config.storageIdentifier },
                    });
                    this.logger.trace('End signOut (No ID token)');
                    resolve();
                }
            });
        });
    }
    /**
     * Ends a users session with the application.  The access token is revoked, and the users session with Okta
     *   is DELETEd, however this does not log the user out of Okta.
     *
     * This function should be used when a sign out has been detected on another tab and you need to sign the user
     *   out of your application.
     *
     * @returns Promise<boolean> indicating if the application session was ended successfully
     */
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Start endSession');
            if (!this.authClient.tokenManager) {
                this.logger.trace('End endSession (No token manager)');
                return false;
            }
            const accessToken = yield this.getAccessToken();
            this.authClient.tokenManager.clear();
            if (accessToken) {
                yield this.authClient.token.revoke(accessToken);
            }
            yield this.authClient.closeSession();
            this.logger.trace('End endSession (Success)');
            return true;
        });
    }
    /**
     * @deprecated since version 3.0. Please transition to {@link acquireUserTokens}
     *
     * Returns an async boolean value that will let client code know if a redirect is going to happen. If false, no
     * redirect has been queued. If true, the SDK will be redirecting the window soon, so client apps should just not
     * perform business logic.
     *
     * If an error occurs, this will return an {@link MGMAuthError} instead of a boolean. Check for it like so:
     *
     * @example
     *
     * const redirectingOrError = await client.acceptTokens({ redirect: false });
     *
     * if (redirectingOrError instanceof MGMAuthError) {
     *   // handle error
     * } else if (redirectingOrError) {
     *    // the token is available, sdk is redirecting to tokensAcceptedRedirectUri
     * } else {
     *    // the token is available, no redirect is occurring
     * }
     */
    acceptTokens(options) {
        return __awaiter(this, void 0, void 0, function* () {
            // Any redirect coming back from the authentication app will no longer pass back a code,
            //  since we use the setCookieAndRedirect method which already sets the cookie for us.
            if (!window.location.search || (window.location.search && !window.location.search.match(/[?|&]code=/))) {
                const hasValidSession = yield this.isSessionValid();
                return hasValidSession
                    ? undefined
                    : new index_1.MGMAuthError({ message: `Unable to parse tokens from URL: ${window.location.href}` });
            }
            const token = yield this.getAccessToken();
            const shouldRedirect = !(token === null || token === void 0 ? void 0 : token.isTransient) && (!options || (options && !options.forceNoRedirect));
            if (token) {
                if (shouldRedirect) {
                    // if requested, redirect to the `tokensAcceptedRedirectUri`, otherwise just exit
                    this.performTokenAcceptedRedirect();
                    return;
                }
                // exit with valid token, but let client know we're not redirecting
                return;
            }
            try {
                // if so we need to parse the tokens from the URL
                const { tokens } = yield this.authClient.token.parseFromUrl();
                this.authClient.tokenManager.setTokens(tokens);
                // if requested, redirect to the `tokensAcceptedRedirectUri`
                if (shouldRedirect) {
                    this.performTokenAcceptedRedirect();
                    return;
                }
                return;
            }
            catch (e) {
                if (e instanceof Error) {
                    return new index_1.MGMAuthError(e);
                }
                else if (typeof e === 'string') {
                    return new index_1.MGMAuthError({ message: e });
                }
            }
            return new index_1.MGMAuthError({ message: `Unable to parse tokens from URL: ${window.location.href}` });
        });
    }
    /**
     * Signs the user in using their credentials.
     *
     * @param options The credentials for the user
     * @param postAuthCallback A callback that happens after authentication, but before the redirect.
     *
     * @returns An MGMAuthError if something fails during this process
     *
     * Possible Exceptions
     * - Failed to authenticate: The authentication request did not return success (includes the transaction object)
     * - Unable to sign in: The user has a stop code that prevents them from being able to log in
     * - Operation Timeout: the call to logout did not complete within the alloted time frame (10 seconds)
     * - Failed to authenticate, check your email and password and try again: The credentials provided are incorrect
     * - Failed to authenticate, please try again later: An unknown error occurred from the server
     */
    signIn(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.logger.trace('Start signIn');
            try {
                const result = yield this.authClient.signInWithCredentials(Object.assign(Object.assign({}, options), { 
                    // TODO: Validate if these do anything by requesting a user to reset their password upon next login
                    options: {
                        multiOptionalFactorEnroll: true,
                        warnBeforePasswordExpired: true,
                    } }));
                if (result.status !== 'SUCCESS' || result.sessionToken === undefined) {
                    this.logger.trace('End signIn (Non Success/No session token)');
                    return new index_1.MGMAuthError({ message: 'Failed to authenticate', transaction: result });
                }
                if (this.config.profileVerificationUrl && options.username) {
                    const url = this.config.profileVerificationUrl.replace('{email}', options.username);
                    const response = yield (0, http_1.get)(url, undefined, {
                        credentials: 'include',
                    });
                    if (!response.ok) {
                        const json = yield response.json();
                        const apiError = json;
                        // if the user has an active stop code
                        if ((apiError === null || apiError === void 0 ? void 0 : apiError.errorCode) === errors_1.ApiErrors.stopCode.errorCode) {
                            this.logger.trace('End signIn (Stop code)');
                            return new index_1.MGMAuthError({
                                message: errors_1.ApiErrors.stopCode.message,
                            });
                        }
                    }
                }
                if (!this.config.sessionCreatedRedirectUrl) {
                    return new index_1.MGMAuthError({ message: 'sessionCreatedRedirectUrl is missing' });
                }
                return new Promise((resolve, reject) => {
                    var _a;
                    const issuerOrigin = (_a = this.config.issuer) === null || _a === void 0 ? void 0 : _a.split('/oauth2/')[0];
                    const signInRedirect = new URL(`${issuerOrigin}/login/sessionCookieRedirect`);
                    signInRedirect.searchParams.append('checkAccountSetupComplete', 'true');
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    signInRedirect.searchParams.append('token', result.sessionToken);
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    signInRedirect.searchParams.append('redirectUrl', this.config.sessionCreatedRedirectUrl);
                    const signInSuccess = () => {
                        // get the users new token
                        this.performSilentLogin(false).then(() => {
                            this.crossTab.emit({ key: 'sign-in', data: {} });
                            resolve();
                        });
                    };
                    this.loadOktaIframe(signInRedirect.toString(), 'Session Acquired', (e) => {
                        // check if the session was created
                        this.hasActiveMemberSession().then(({ activeMemberSession }) => {
                            if (activeMemberSession) {
                                this.logger.trace('End signIn (Success - Timeout, but active session)');
                                signInSuccess();
                            }
                            else {
                                this.logger.trace('End signIn (Error State)');
                                reject(e);
                            }
                        });
                    }, () => {
                        this.logger.trace('End signIn (Success)');
                        signInSuccess();
                    });
                });
            }
            catch (e) {
                if (e instanceof okta_auth_js_1.AuthApiError) {
                    if (e.errorCode === errors_1.ApiErrors.authenticationFailed.errorCode) {
                        this.logger.trace('End signIn (Wrong Credentials)');
                        if (this.config.clientId === 'bet_mgm') {
                            const urlParams = (0, utils_1.getUrlParams)();
                            return new index_1.MGMAuthError({
                                message: ((_a = errors_1.ApiErrors.authenticationFailed.betmgmMessage) === null || _a === void 0 ? void 0 : _a.replaceAll('{REDIRECT_URI}', urlParams === null || urlParams === void 0 ? void 0 : urlParams.redirectUri)) ||
                                    errors_1.ApiErrors.authenticationFailed.message,
                            });
                        }
                        return new index_1.MGMAuthError({ message: errors_1.ApiErrors.authenticationFailed.message });
                    }
                }
                if (e instanceof Error) {
                    this.logger.error(e);
                }
                this.logger.trace('End signIn (Error State)');
                return new index_1.MGMAuthError({ message: 'Failed to authenticate, please try again later' });
            }
        });
    }
    /**
     * Sign up a user for a new account
     *
     * @param user the users information that will be used to create the account
     *
     * @returns An IdentityResponse or IdentityError
     */
    signUp(user) {
        return __awaiter(this, void 0, void 0, function* () {
            const postObject = {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                login: user.email,
                mobilePhone: user.phone,
                birthDate: `${user.birthDate.getFullYear()}-${user.birthDate.getMonth() + 1}-${user.birthDate.getDate()}`,
                credentials: { password: user.password },
                canadianOptIn: user.canadianOptIn,
                address: user.address,
                partner: user.partner,
            };
            const response = yield (0, http_1.post)(`${this.config.directoryServiceUrl}/guest?enrollLoyalty=true`, postObject, undefined, {
                credentials: 'include',
            });
            return this.processIdentityResponse(response);
        });
    }
    /**
     *
     */
    activateWhileBooking(user_1) {
        return __awaiter(this, arguments, void 0, function* (user, source = 'jwb') {
            const postObject = {
                login: user.email,
                email: user.email,
                birthDate: `${user.birthDate.getFullYear()}-${user.birthDate.getMonth() + 1}-${user.birthDate.getDate()}`,
                firstName: user.firstName,
                lastName: user.lastName,
                mobilePhone: user.phone,
                address: user.address,
                credentials: { password: user.password },
                canadianOptIn: user.canadianOptIn,
            };
            const _source = source === 'jwb' ? '' : '&source=jab';
            const response = yield (0, http_1.post)(`${this.config.directoryServiceUrl}/guest?jwb=true${_source}`, postObject, undefined, {
                credentials: 'include',
            });
            return this.processIdentityResponse(response);
        });
    }
    // eslint-disable-next-line no-undef
    processIdentityResponse(response) {
        return __awaiter(this, void 0, void 0, function* () {
            const commonReturn = { status: response.status, headers: response.headers };
            if ([204].includes(response.status)) {
                return Object.assign({ response: {} }, commonReturn);
            }
            const json = yield response.json();
            if ([200, 201, 202, 203].includes(response.status)) {
                return Object.assign({ response: json }, commonReturn);
            }
            if (!json.errorCode || (json.errorCode && !(json.errorCode in errors_1.KNOWN_ERROR_CODES))) {
                throw Error(errors_1.UNKNOWN_ERROR_MESSAGE);
            }
            if (json.errorCode && json.errorCode in errors_1.KNOWN_ERROR_CODES) {
                throw new index_1.IdentityError(errors_1.KNOWN_ERROR_CODES[json.errorCode], json.errorCode);
            }
            return Object.assign({ response: json }, commonReturn);
        });
    }
    performTokenAcceptedRedirect() {
        this.logger.trace('Start performTokenAcceptedRedirect');
        if (!this.config.tokensAcceptedRedirectUri) {
            this.logger.warn('[MGMAuth]: redirect failed due to missing value for tokensAcceptedRedirectUri');
            this.logger.trace('End performTokenAcceptedRedirect (Misconfiguration');
            return;
        }
        this.logger.trace('End performTokenAcceptedRedirect (Redirecting');
        window.location.href = this.config.tokensAcceptedRedirectUri;
    }
    /**
     * Verifies if an active session for the transient user is present
     *
     * @returns a boolean indicating if a transient session is active
     */
    isTransientSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Start isTransientSession');
            try {
                const session = yield this.authClient.session.get();
                if (!this.sessionIsActive(session)) {
                    this.logger.trace('End isTransientSession (Inactive Session)');
                    return false;
                }
                if (this.sessionHasTransientUser(session)) {
                    this.logger.trace('Start isTransientSession (Transient Session)');
                    return true;
                }
                this.logger.trace('Start isTransientSession (Default State)');
                return false;
            }
            catch (e) {
                if (e instanceof Error) {
                    this.logger.debug(`Error in checking session: ${e.message}`);
                }
            }
            this.logger.trace('Start isTransientSession (Error State)');
            return false;
        });
    }
    /**
     * Checks to see if a valid session exists.  If a valid session exists, the existing token is also checked to verify
     * that the token matches the session user.  If the token does not match the session, then a new token is acquired.
     *
     * @param allowTransientForSilentAuth if true, then we will allow a transient session.  If false, then it must be a full user session
     *
     * @returns true if there is an active session and the token has been updated to match the session (transient if allowed). Otherwise returns false
     */
    isSessionValid() {
        return __awaiter(this, arguments, void 0, function* (allowTransientForSilentAuth = false) {
            this.logger.trace('Starting isSessionValid');
            try {
                const session = yield this.authClient.session.get();
                if (!this.sessionIsActive(session)) {
                    this.logger.trace('End isSessionValid (Inactive Session)');
                    return false;
                }
                if ((this.sessionHasTransientUser(session) && !allowTransientForSilentAuth) ||
                    window.location.pathname === '/implicit/callback') {
                    this.logger.trace('End isSessionValid (Not allowing transient sessions)');
                    return false;
                }
                const token = (yield this.authClient.tokenManager.get(index_1.tokenKeys.accessToken));
                const isTokenValid = this.isTokenValid(token);
                let hasSessionChanged = false;
                if (isTokenValid) {
                    if (token.claims) {
                        hasSessionChanged = token.claims.sub !== session.login;
                    }
                    else {
                        const claims = yield this.authClient.getUser();
                        hasSessionChanged = claims.sub !== session.login;
                    }
                }
                if (!isTokenValid || hasSessionChanged) {
                    yield this.performSilentLogin(this.sessionHasTransientUser(session));
                }
                this.logger.trace('End isSessionValid (Success)');
                return true;
            }
            catch (e) {
                if (e instanceof Error) {
                    this.logger.debug(`Error in checking session: ${e.message}`);
                }
            }
            this.logger.trace('End isSessionValid (Error State)');
            return false;
        });
    }
    /**
     * Checks to see if an active session exists that is not transient
     *
     * @returns an object that contains
     * - activeMemberSession: if there is an active session that is not transient
     * - session: the session object from Okta
     */
    hasActiveMemberSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Starting hasActiveMemberSession');
            try {
                const session = yield this.authClient.session.get();
                if (this.sessionIsActive(session) && !this.sessionHasTransientUser(session)) {
                    this.logger.trace('End hasActiveMemberSession (Session Present)');
                    return { activeMemberSession: true, session };
                }
            }
            catch (e) { }
            this.logger.trace('End hasActiveMemberSession (No Session/Transient Session)');
            return { activeMemberSession: false, session: null };
        });
    }
    /**
     * @deprecated since version 3.0. Please transition to {@link onTokenExpired}, {@link onTokenRenewed}, {@link onTokenError}
     *
     * Allows you to provide a callback function that will be triggered when one of several
     *
     * @param hook A callback that will be tied to the 'expired' | 'renewed' | 'error' events from Okta's token manager
     */
    setListeners(hook) {
        const { authClient } = this;
        const handleError = error => hook(okta_auth_js_1.EVENT_ERROR, {}, error);
        const handleExpired = (key, expiredToken) => hook(okta_auth_js_1.EVENT_EXPIRED, { key, expiredToken });
        const handleRenewed = (key, newToken, oldToken) => hook(okta_auth_js_1.EVENT_RENEWED, { key, nextToken: newToken, oldToken });
        // Fired before a token is set to expire
        authClient.tokenManager.on(okta_auth_js_1.EVENT_EXPIRED, handleExpired);
        // Triggered when a token has been renewed
        authClient.tokenManager.on(okta_auth_js_1.EVENT_RENEWED, handleRenewed);
        // Triggered when an OAuthError is returned via the API (typically during token renew)
        authClient.tokenManager.on(okta_auth_js_1.EVENT_ERROR, handleError);
    }
    /**
     * Get the raw Okta Auth client.
     *
     * @returns The Okta auth client
     */
    getAuthClient() {
        return this.authClient;
    }
    getTransientScopes() {
        var _a;
        const scopes = new Set([...((_a = this.config.scopes) !== null && _a !== void 0 ? _a : []), ...index_1.transientScopes, ...index_1.commonScopes]);
        index_1.scopesOnlyForAuthedUsers.forEach(scope => scopes.delete(scope));
        return [...scopes];
    }
    getAuthenticatedScopes() {
        var _a;
        const scopes = new Set([...((_a = this.config.scopes) !== null && _a !== void 0 ? _a : []), ...index_1.scopesOnlyForAuthedUsers, ...index_1.commonScopes]);
        return [...scopes];
    }
    // eslint-disable-next-line no-undef
    getAnonTokenV1Response() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.config.anonTokenUrl) {
                return yield (0, http_1.get)(this.config.anonTokenUrl);
            }
            return null;
        });
    }
    // eslint-disable-next-line no-undef
    getAnonTokenResponse() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.config.anonTokenV2Url) {
                try {
                    const response = yield (0, http_1.get)(this.config.anonTokenV2Url);
                    if (response.ok) {
                        return response;
                    }
                }
                catch (_a) {
                    this.logger.trace('Failed to retrieve the anon token through V2 API falling back to the V1');
                }
            }
            return this.getAnonTokenV1Response();
        });
    }
    getCommonTransientToken() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace('Starting getCommonTransientToken');
            try {
                const response = yield this.getAnonTokenResponse();
                if (response && response.ok) {
                    const json = yield response.json();
                    const res = json;
                    const tokens = {};
                    const tokenType = res.token_type;
                    const accessToken = res.access_token;
                    const scopes = res.scope ? res.scope.split(' ') : this.getTransientScopes();
                    if (accessToken) {
                        const accessJwt = this.authClient.token.decode(accessToken);
                        tokens.accessToken = {
                            accessToken: accessToken,
                            claims: accessJwt.payload,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            expiresAt: accessJwt.payload.exp,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            tokenType: tokenType,
                            scopes: scopes,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            authorizeUrl: this.config.authorizeUrl,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            userinfoUrl: this.config.userinfoUrl,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        };
                    }
                    if (tokens && this.isTokenValid(tokens.accessToken)) {
                        this.authClient.tokenManager.setTokens(tokens);
                        this.logger.trace('End getCommonTransientToken');
                        return true;
                    }
                }
            }
            catch (e) {
                this.logger.debug('Error acquiring common anon token');
            }
            this.logger.trace('End getCommonTransientToken (failed)');
            return false;
        });
    }
    performSilentLogin() {
        return __awaiter(this, arguments, void 0, function* (isTransientFlow = false) {
            try {
                this.logger.trace('Starting performSilentLogin');
                if (isTransientFlow) {
                    const tokenAcquired = yield this.getCommonTransientToken();
                    if (tokenAcquired) {
                        this.logger.trace('End performSilentLogin (common token)');
                        return;
                    }
                    else {
                        this.logger.debug('performSilentLogin unable to acquire common anon token');
                    }
                }
                // We inject sign in specific scopes
                const options = isTransientFlow
                    ? { scopes: this.getTransientScopes() }
                    : { scopes: this.oktaConfig.scopes };
                // We need a valid redirect URL although this process does not actually do a redirect.
                //  Since this is posting messages back, we also need to make sure that it matches the origin
                //  of the requesting page, otherwise the postMessage will not send back to let Okta know it
                //  has a new token.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const tokenRedirectUri = new URL(this.config.tokensAcceptedRedirectUri);
                tokenRedirectUri.host = window.location.host;
                tokenRedirectUri.protocol = window.location.protocol;
                if (this.config.allowOktaSignInWithRedirect) {
                    const urlParams = (0, utils_1.getUrlParams)();
                    // TODO: Hardcode the bet-mgm redirect URI as an environment variable. It's less secure to accept a redirect URI from the query parameters
                    options.redirectUri = urlParams.redirectUri;
                    options.codeChallenge = urlParams.codeChallenge;
                    options.state = urlParams.state;
                    options.scopes = ['profile:read', 'openid'];
                    yield this.authClient.signInWithRedirect(options);
                }
                else {
                    options.redirectUri = tokenRedirectUri.toString();
                    const { tokens } = yield this.authClient.token.getWithoutPrompt(options);
                    this.authClient.tokenManager.setTokens(tokens);
                }
                this.logger.trace('End performSilentLogin');
            }
            catch (e) {
                this.logger.error(e, { message: 'Failed to perform silent login' });
                throw Error('[MGMAuth] Failed to perform silent login');
            }
        });
    }
    isTokenValid(token) {
        return !(!token || (token && this.authClient.tokenManager.hasExpired(token)));
    }
    sessionIsActive(session) {
        if ((session === null || session === void 0 ? void 0 : session.status) === "ACTIVE" /* SessionStatus.active */) {
            return true;
        }
        return false;
    }
    sessionHasTransientUser(session) {
        if ((session === null || session === void 0 ? void 0 : session.login) === index_1.TRANSIENT_CID) {
            return true;
        }
        return false;
    }
}
exports.MGMAuth = MGMAuth;
